import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import GoogleButton from "../../components/GoogleButton";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/constants";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const next = params.get("next")?.startsWith("/") ? params.get("next") : "/home";
  const [error, setError] = useState(
    params.get("google") === "failed"
      ? "Google sign-in was cancelled or failed. Please try again."
      : params.get("expired")
      ? "Your session expired. Please sign in again — your cart is saved."
      : ""
  );
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ident || !password) {
      setError("Please enter both fields to continue.");
      return;
    }
    setLoading(true);
    try {
      await login(ident, password);
      navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to start your market order.">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div
            data-testid="login-error"
            className="flex items-start gap-2 rounded-xl bg-[#FFF0F0] border border-[#FFD0D0] px-4 py-3 text-sm text-[#991B1B]"
          >
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <div className="relative">
          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="login-identifier-input"
            type="text"
            placeholder="Email or phone"
            value={ident}
            onChange={(e) => setIdent(e.target.value)}
            className="w-full h-[52px] pl-11 pr-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors"
          />
        </div>
        <div className="relative">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="login-password-input"
            type={show ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-[52px] pl-11 pr-11 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors"
          />
          <button
            type="button"
            data-testid="toggle-password"
            onClick={() => setShow(!show)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div className="text-right">
          <Link
            data-testid="forgot-password-link"
            to="/forgot-password"
            className="text-sm text-[#228B22] font-semibold hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={loading}
          className="w-full h-[52px] rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200 flex items-center justify-center disabled:opacity-60"
        >
          {loading ? <Loader2 size={20} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-gray-500">
        New to Ayome?{" "}
        <Link data-testid="go-register-link" to="/register" className="text-[#228B22] font-semibold hover:underline">
          Create an account
        </Link>
      </p>
      <p className="mt-8 text-center text-xs text-gray-400">
        <Link to="/admin" className="hover:text-gray-600">Admin access</Link>
      </p>
    </AuthShell>
  );
}
